<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <footer
    class="app-footer"
    role="contentinfo"
    itemscope
    itemtype="https://schema.org/Organization"
  >
    <div class="footer-container">
      <!-- Company Info Section -->
      <div class="footer-section footer-brand">
        <h3 class="footer-logo" itemprop="name">n.writer</h3>
        <p class="footer-tagline" itemprop="description">
          {{ $t('footer.tagline', 'AI-powered writing assistant for everyone') }}
        </p>
        <!-- Hidden structured data for SEO -->
        <meta itemprop="url" content="https://n-writer.app" />
        <link itemprop="logo" href="https://n-writer.app/images/noorwriter-logo.png" />
      </div>

      <!-- Product Links -->
      <nav class="footer-section" aria-label="Product navigation">
        <h4 class="footer-heading">{{ $t('footer.product', 'Product') }}</h4>
        <ul class="footer-links" role="list">
          <li>
            <router-link to="/editor">{{ $t('navigation.editor') }}</router-link>
          </li>
          <li>
            <router-link to="/pricing">{{ $t('navigation.pricing') }}</router-link>
          </li>
          <li>
            <router-link to="/extension">{{ $t('navigation.extension') }}</router-link>
          </li>
          <li>
            <router-link to="/grammar-checker">{{
              $t('home.seoFeatures.grammar.title', 'Grammar Checker')
            }}</router-link>
          </li>
          <li>
            <router-link to="/plagiarism-checker">{{
              $t('home.seoFeatures.plagiarism.title', 'Plagiarism Checker')
            }}</router-link>
          </li>
        </ul>
      </nav>

      <!-- Resources Links -->
      <nav class="footer-section" aria-label="Resources navigation">
        <h4 class="footer-heading">{{ $t('footer.resources', 'Resources') }}</h4>
        <ul class="footer-links" role="list">
          <li>
            <router-link to="/support">{{ $t('navigation.support') }}</router-link>
          </li>
          <li>
            <router-link to="/ai-writing-assistant">{{
              $t('home.seoFeatures.writing.title', 'Writing Assistant')
            }}</router-link>
          </li>
          <li>
            <router-link to="/ai-content-detector">{{
              $t('home.seoFeatures.aiDetection.title', 'AI Detector')
            }}</router-link>
          </li>
          <li>
            <router-link to="/citation-checker">{{
              $t('home.seoFeatures.citations.title', 'Citation Checker')
            }}</router-link>
          </li>
        </ul>
      </nav>

      <!-- Connect Section -->
      <div class="footer-section footer-connect">
        <h4 class="footer-heading">{{ $t('footer.connect', 'Connect') }}</h4>
        <address
          class="footer-contact"
          itemprop="contactPoint"
          itemscope
          itemtype="https://schema.org/ContactPoint"
        >
          <meta itemprop="contactType" content="customer service" />
          <a href="mailto:contact@noorways.fr" class="footer-email" itemprop="email">
            <svg
              class="footer-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            contact@noorways.fr
          </a>
        </address>
        <!-- Social Links -->
        <div class="footer-social" role="list" aria-label="Social media links">
          <a
            href="https://www.linkedin.com/company/noorways"
            target="_blank"
            rel="noopener noreferrer"
            class="social-link"
            aria-label="Follow NoorWays on LinkedIn"
            itemprop="sameAs"
          >
            <svg class="social-icon" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
              />
            </svg>
          </a>
          <a
            href="https://chromewebstore.google.com/detail/fjlfoaokggeemlcoemkhokccampibipm"
            target="_blank"
            rel="noopener noreferrer"
            class="social-link"
            aria-label="Get n.writer on Chrome Web Store"
          >
            <svg class="social-icon" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="10" fill="#4285F4" />
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"
                fill="none"
              />
              <path
                d="M12 7c-2.76 0-5 2.24-5 5h3c0-1.1.9-2 2-2s2 .9 2 2c0 1.1-.9 2-2 2v3c2.76 0 5-2.24 5-5s-2.24-5-5-5z"
                fill="#EA4335"
              />
              <path
                d="M7 12c0-1.38.56-2.63 1.46-3.54L5.64 5.64A9.95 9.95 0 0 0 2 12h5z"
                fill="#34A853"
              />
              <path
                d="M12 17c-1.38 0-2.63-.56-3.54-1.46l-2.82 2.82A9.95 9.95 0 0 0 12 22v-5z"
                fill="#FBBC05"
              />
              <circle cx="12" cy="12" r="3" fill="white" />
            </svg>
          </a>
        </div>
      </div>
    </div>

    <!-- Bottom Bar -->
    <div class="footer-bottom">
      <div class="footer-bottom-container">
        <p class="footer-copyright">
          &copy; {{ currentYear }} NoorWays.
          {{ $t('footer.rightsReserved', 'All rights reserved.') }}
        </p>
        <nav class="footer-legal" aria-label="Legal navigation">
          <router-link to="/privacy">{{
            $t('footer.privacyPolicy', 'Privacy Policy')
          }}</router-link>
          <span class="separator">•</span>
          <span class="powered-by"
            >{{ $t('footer.poweredBy', 'A product by') }}
            <a href="https://noorways.fr" target="_blank" rel="noopener noreferrer"
              >noorways</a
            ></span
          >
        </nav>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const currentYear = computed(() => new Date().getFullYear())
</script>

<style lang="scss" scoped>
.app-footer {
  background: var(--nw-brand-slate, #1e293b);
  color: var(--nw-white);
  margin-top: auto;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem 3rem;
  display: grid;
  grid-template-columns: 1.5fr repeat(3, 1fr);
  gap: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 3rem 1.5rem 2rem;
  }
}

.footer-section {
  display: flex;
  flex-direction: column;
}

.footer-brand {
  .footer-logo {
    font-size: 1.5rem;
    font-weight: 800;
    margin: 0 0 0.75rem;
    background: linear-gradient(135deg, var(--nw-primary-light) 0%, var(--nw-primary) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .footer-tagline {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.95rem;
    line-height: 1.6;
    margin: 0;
    max-width: 280px;
  }
}

.footer-heading {
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--nw-white);
  margin: 0 0 1.25rem;
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  li a {
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    display: inline-block;

    &:hover {
      color: var(--nw-primary-light);
      transform: translateX(4px);
    }
  }
}

.footer-connect {
  .footer-contact {
    font-style: normal;
    margin-bottom: 1.25rem;
  }

  .footer-email {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &:hover {
      color: var(--nw-primary-light);
    }

    .footer-icon {
      width: 1rem;
      height: 1rem;
    }
  }
}

.footer-social {
  display: flex;
  gap: 0.75rem;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;

  &:hover {
    background: var(--nw-primary);
    color: var(--nw-white);
    transform: translateY(-2px);
  }

  .social-icon {
    width: 1.125rem;
    height: 1.125rem;
  }
}

.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-bottom-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 2rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
    padding: 1.25rem 1.5rem;
  }
}

.footer-copyright {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.875rem;
  margin: 0;
}

.footer-legal {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;

  a {
    color: rgba(255, 255, 255, 0.5);
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: var(--nw-primary-light);
    }
  }

  .separator {
    color: rgba(255, 255, 255, 0.3);
  }

  .powered-by {
    color: rgba(255, 255, 255, 0.5);

    a {
      color: var(--nw-primary-light);
      font-weight: 600;

      &:hover {
        color: var(--nw-white);
      }
    }
  }
}
</style>
